import React, { useEffect, useState } from "react";
import Layout from "./layout";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { doFetch, clearSearchResults } from "../redux/api/actions";
import { SEARCH_TOURS } from "../redux/api/constants";
import { displayCostType } from "./utils";

const TourCard = ({ tour, searchDate, searchGuests }) => {
    const navigate = useNavigate();
    const imageUrls = tour.image_urls ? tour.image_urls.split(",").filter(url => url.trim()) : [];
    
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const handleClick = () => {
        navigate(`/tours/${tour.id}`);
    };

    return (
        <div className="search-tour-card" onClick={handleClick}>
            <div className="tour-card-image">
                {imageUrls.length > 0 ? (
                    <img src={imageUrls[0]} alt={tour.title} />
                ) : (
                    <div className="no-image">
                        <i className="bi bi-image"></i>
                    </div>
                )}
            </div>
            <div className="tour-card-content">
                <div className="tour-card-header">
                    <h3>{tour.title}</h3>
                    <div className="tour-card-price">
                        <span className="price">${tour.cost}</span>
                        <span className="price-type">/ {displayCostType(tour.cost_type)}</span>
                    </div>
                </div>
                
                <p className="tour-card-description">{tour.description}</p>
                
                <div className="tour-card-meta">
                    <span><i className="bi bi-clock"></i> {tour.duration_in_hours} hrs</span>
                    <span><i className="bi bi-people"></i> Up to {tour.max_tour_size}</span>
                </div>

                {tour.available_events && tour.available_events.length > 0 && (
                    <div className="tour-card-availability">
                        <div className="availability-label">
                            <i className="bi bi-calendar-check"></i>
                            Available dates:
                        </div>
                        <div className="availability-dates">
                            {tour.available_events.slice(0, 3).map((event, idx) => (
                                <span key={idx} className="date-chip">
                                    {formatDate(event.tour_date)} at {formatTime(event.tour_date)}
                                </span>
                            ))}
                            {tour.available_events.length > 3 && (
                                <span className="more-dates">+{tour.available_events.length - 3} more</span>
                            )}
                        </div>
                    </div>
                )}

                {tour.tourguide && (
                    <div className="tour-card-host">
                        {tour.tourguide.avatar_url && (
                            <img src={tour.tourguide.avatar_url} alt={tour.tourguide.name} className="host-avatar-small" />
                        )}
                        <span>Hosted by {tour.tourguide.name}</span>
                    </div>
                )}
            </div>
        </div>
    );
};

const SearchFilters = ({ date, setDate, guests, setGuests, onSearch }) => {
    const getMinDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    return (
        <div className="search-filters">
            <div className="filter-group">
                <label>Date</label>
                <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    min={getMinDate()}
                />
            </div>
            <div className="filter-group">
                <label>Guests</label>
                <div className="guests-input">
                    <button onClick={() => setGuests(Math.max(1, guests - 1))} disabled={guests <= 1}>
                        <i className="bi bi-dash"></i>
                    </button>
                    <span>{guests}</span>
                    <button onClick={() => setGuests(Math.min(20, guests + 1))} disabled={guests >= 20}>
                        <i className="bi bi-plus"></i>
                    </button>
                </div>
            </div>
            <button className="filter-search-btn" onClick={onSearch}>
                Update Results
            </button>
        </div>
    );
};

const Location = () => {
    const { location } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    
    const searchResults = useSelector(state => state.API.searchResults);
    const loading = useSelector(state => state.API.loading);
    const dispatch = useDispatch();

    // Parse location from URL
    const parseLocation = (location) => {
        const locationArray = location.split("--");
        const city = locationArray[0].replace(/-/g, " ");
        let state = "";
        let country = "";
        
        if (locationArray.length === 2) {
            country = locationArray[1].replace(/-/g, " ");
        } else if (locationArray.length === 3) {
            state = locationArray[1] ? locationArray[1].replace(/-/g, " ") : "";
            country = locationArray[2].replace(/-/g, " ");
        }
        
        return { city, state, country };
    };

    const { city, state, country } = parseLocation(location);
    
    // Get filters from URL params
    const [date, setDate] = useState(searchParams.get('date') || '');
    const [guests, setGuests] = useState(parseInt(searchParams.get('guests')) || 1);

    useEffect(() => {
        // Build search query
        let query = `locations/search?city=${encodeURIComponent(city)}`;
        if (state) query += `&state=${encodeURIComponent(state)}`;
        if (country) query += `&country=${encodeURIComponent(country)}`;
        if (date) query += `&date=${date}`;
        query += `&guests=${guests}`;

        dispatch(doFetch(SEARCH_TOURS, query, null));

        return () => {
            dispatch(clearSearchResults());
        };
    }, [dispatch, city, state, country, date, guests]);

    const handleSearch = () => {
        const params = new URLSearchParams();
        if (date) params.append('date', date);
        if (guests > 1) params.append('guests', guests);
        setSearchParams(params);
    };

    const locationDisplay = searchResults?.location 
        ? `${searchResults.location.city}${searchResults.location.state ? `, ${searchResults.location.state}` : ''}`
        : `${city}${state ? `, ${state}` : ''}`;

    const countryFlag = searchResults?.location?.flag || '';

    return (
        <Layout selected="location">
            <div className="search-results-page">
                <div className="search-results-header">
                    <nav aria-label="breadcrumb" className="breadcrumb-nav">
                        <ol className="breadcrumb">
                            <li className="breadcrumb-item">
                                <Link to="/">Home</Link>
                            </li>
                            <li className="breadcrumb-item active">{locationDisplay}</li>
                        </ol>
                    </nav>
                    
                    <div className="location-hero">
                        <h1>
                            Experiences in {locationDisplay} {countryFlag}
                        </h1>
                        <p className="results-count">
                            {searchResults?.tours?.length || 0} tours available
                            {date && ` around ${new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                            {guests > 1 && ` for ${guests} guests`}
                        </p>
                    </div>

                    <SearchFilters
                        date={date}
                        setDate={setDate}
                        guests={guests}
                        setGuests={setGuests}
                        onSearch={handleSearch}
                    />
                </div>

                <div className="search-results-content">
                    {loading ? (
                        <div className="loading-state">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : searchResults?.tours && searchResults.tours.length > 0 ? (
                        <div className="tours-grid-search">
                            {searchResults.tours.map((tour) => (
                                <TourCard 
                                    key={tour.id} 
                                    tour={tour}
                                    searchDate={date}
                                    searchGuests={guests}
                                />
                            ))}
                        </div>
                    ) : (
                        <div className="empty-results">
                            <i className="bi bi-compass"></i>
                            <h3>No tours found</h3>
                            <p>
                                {date 
                                    ? "Try adjusting your dates or search without a date to see all available tours."
                                    : "There are no tours available in this location yet. Check back soon!"
                                }
                            </p>
                            {date && (
                                <button 
                                    className="btn-outline"
                                    onClick={() => { setDate(''); handleSearch(); }}
                                >
                                    Clear date filter
                                </button>
                            )}
                            <Link to="/tourguide/about" className="btn-primary mt-3">
                                Become a Tour Guide Here
                            </Link>
                        </div>
                    )}
                </div>
            </div>
        </Layout>
    );
};

export default Location;
